import { router } from '@inertiajs/react';
import { ChevronDown, Search, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FeedbackFilters } from '@/types';

interface FeedbackFiltersProps {
  eventSlug: string;
  initialParams: FeedbackFilters;
}

const ANY = 'any';

function rangeOptions(min: number, max: number) {
  const options: number[] = [];
  for (let i = min; i <= max; i++) options.push(i);
  return options;
}

function toSelectValue(n: number | null): string {
  return n == null ? ANY : String(n);
}

export function FeedbackSearch({
  eventSlug,
  initialParams,
}: FeedbackFiltersProps) {
  const [isPending, setIsPending] = useState(false);

  const [search, setSearch] = useState(initialParams.search ?? '');
  const [ratingMin, setRatingMin] = useState(
    toSelectValue(initialParams.ratingMin),
  );
  const [ratingMax, setRatingMax] = useState(
    toSelectValue(initialParams.ratingMax),
  );
  const [npsMin, setNpsMin] = useState(toSelectValue(initialParams.npsMin));
  const [npsMax, setNpsMax] = useState(toSelectValue(initialParams.npsMax));

  const hasFilters =
    initialParams.ratingMin != null ||
    initialParams.ratingMax != null ||
    initialParams.npsMin != null ||
    initialParams.npsMax != null;

  const [open, setOpen] = useState(hasFilters);

  const buildParams = (overrides: Record<string, string | undefined> = {}) => {
    const params = new URLSearchParams(window.location.search);
    const set = (key: string, value: string | undefined) => {
      if (value && value !== '' && value !== ANY) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    };
    set('search', overrides.search ?? (search.trim() || undefined));
    set(
      'ratingMin',
      overrides.ratingMin ?? (ratingMin === ANY ? undefined : ratingMin),
    );
    set(
      'ratingMax',
      overrides.ratingMax ?? (ratingMax === ANY ? undefined : ratingMax),
    );
    set('npsMin', overrides.npsMin ?? (npsMin === ANY ? undefined : npsMin));
    set('npsMax', overrides.npsMax ?? (npsMax === ANY ? undefined : npsMax));
    params.delete('page');
    return params;
  };

  const visit = (url: string) => {
    router.get(
      url,
      {},
      {
        onStart: () => setIsPending(true),
        onFinish: () => setIsPending(false),
      },
    );
  };

  const apply = () => {
    const params = buildParams();
    visit(`/admin/${eventSlug}/feedback?${params.toString()}`);
  };

  const reset = () => {
    setSearch('');
    setRatingMin(ANY);
    setRatingMax(ANY);
    setNpsMin(ANY);
    setNpsMax(ANY);
    visit(`/admin/${eventSlug}/feedback`);
  };

  const activeFilterCount =
    (initialParams.ratingMin != null ? 1 : 0) +
    (initialParams.ratingMax != null ? 1 : 0) +
    (initialParams.npsMin != null ? 1 : 0) +
    (initialParams.npsMax != null ? 1 : 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="-translate-y-1/2 absolute top-1/2 left-3 h-4 w-4 transform text-muted-foreground" />
          <Input
            placeholder="Search by hacker name, email, or answers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply();
            }}
            className="pl-10"
          />
        </div>
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </Button>
          </CollapsibleTrigger>
        </Collapsible>
        <Button onClick={apply} disabled={isPending}>
          {isPending ? 'Searching...' : 'Search'}
        </Button>
        {(search || initialParams.search || hasFilters) && (
          <Button variant="outline" onClick={reset} disabled={isPending}>
            Clear
          </Button>
        )}
      </div>

      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleContent>
          <div className="grid grid-cols-1 gap-4 rounded-md border p-4 sm:grid-cols-2">
            <RangeFilter
              label="Overall rating (1–10)"
              min={1}
              max={10}
              fromValue={ratingMin}
              toValue={ratingMax}
              onFromChange={setRatingMin}
              onToChange={setRatingMax}
            />
            <RangeFilter
              label="NPS score (0–10)"
              min={0}
              max={10}
              fromValue={npsMin}
              toValue={npsMax}
              onFromChange={setNpsMin}
              onToChange={setNpsMax}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

interface RangeFilterProps {
  label: string;
  min: number;
  max: number;
  fromValue: string;
  toValue: string;
  onFromChange: (v: string) => void;
  onToChange: (v: string) => void;
}

function RangeFilter({
  label,
  min,
  max,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
}: RangeFilterProps) {
  const options = rangeOptions(min, max);
  return (
    <div>
      <div className="mb-2 font-medium text-sm">{label}</div>
      <div className="flex items-center gap-2">
        <Select value={fromValue} onValueChange={onFromChange}>
          <SelectTrigger>
            <SelectValue placeholder="Min" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any</SelectItem>
            {options.map((n) => (
              <SelectItem key={n} value={String(n)}>
                ≥ {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-muted-foreground text-sm">to</span>
        <Select value={toValue} onValueChange={onToChange}>
          <SelectTrigger>
            <SelectValue placeholder="Max" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any</SelectItem>
            {options.map((n) => (
              <SelectItem key={n} value={String(n)}>
                ≤ {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
